import { useEffect, useMemo, useState } from 'react';
import { TeamMemberQueuePopup } from '@/components/manager/TeamMemberQueuePopup';
import { useTranslation } from 'react-i18next';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import { api } from '@/store/api';
import { TeamMember } from '@/types';
import { useAppSelector } from '@/store/hooks';
import {
  Paper,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  Pill,
  Stack,
  CircularProgress,
} from '@gapartners/ui';
import { getStatusPillColor } from '@/utils/agentStatus';

const {
  useGetDirectReportsQuery,
  useGetUsersRoutingProfilesQuery,
  useGetAgentCcpStatusForUsersQuery,
} = api;

export function TeamView() {
  const { t } = useTranslation();
  const { user } = useAppSelector(state => state.auth);
  // Set page metadata
  usePageMetadata({
    title: t('pages.manager.team.title'),
    description: t('pages.manager.team.description'),
  });
  const getTranslatedCcpStatus = (status?: string) => {
    if (!status) return '—';

    return t(`pages.manager.team.status.${status.toLowerCase()}`);
  };
  const {
    data: members = [],
    refetch: refetchDirectReports,
    isFetching: isFetchingDirectReports,
  } = useGetDirectReportsQuery();
  const membersWithUser = useMemo(() => {
    return [
      ...members,
      {
        mail: user?.email ?? '',
        displayName: user?.name ?? '',
        userPrincipalName: user?.email ?? '',
        id: user?.id ?? '',
        jobTitle: null,
      },
    ];
  }, [members, user]);
  const {
    data: usersRoutingProfiles,
    refetch: refetchUsersRoutingProfiles,
    isFetching: isFetchingRoutingProfiles,
  } = useGetUsersRoutingProfilesQuery(
    {
      userEmails: membersWithUser.map(m => m.mail).filter(Boolean) as string[],
    },
    {
      skip: membersWithUser.map(m => m.mail).filter(Boolean).length === 0,
      refetchOnMountOrArgChange: true,
    }
  );
  const {
    data: agentCcpStatusData,
    refetch: refetchAgentCcpStatus,
    isFetching: isFetchingCcpStatus,
  } = useGetAgentCcpStatusForUsersQuery(
    {
      connectIds: usersRoutingProfiles?.users
        ?.map(m => m.connectId)
        ?.filter(Boolean) as string[],
    },
    {
      skip: !usersRoutingProfiles?.users?.map(m => m.connectId)?.filter(Boolean)
        ?.length,
      refetchOnMountOrArgChange: true,
    }
  );
  const isRefreshing =
    isFetchingDirectReports || isFetchingRoutingProfiles || isFetchingCcpStatus;

  const combinedMembers = useMemo(() => {
    return membersWithUser.map(m => {
      const userRoutingProfile = usersRoutingProfiles?.users.find(
        urp => urp.userEmail === m.mail
      );
      const agentCcpStatus = agentCcpStatusData?.users.find(
        acs => acs.connectId === userRoutingProfile?.connectId
      );
      return {
        ...m,
        routingProfileId: userRoutingProfile?.routingProfileId,
        ccpStatus: agentCcpStatus?.status,
        connectId: userRoutingProfile?.connectId,
      };
    });
  }, [membersWithUser, usersRoutingProfiles, agentCcpStatusData]);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  useEffect(() => {
    if (!combinedMembers.length) {
      setSelectedMember(null);
      return;
    }

    if (selectedMember?.id) {
      const matchedMember = combinedMembers.find(
        m => m.id === selectedMember.id
      );
      setSelectedMember(matchedMember ?? null);
    }
  }, [combinedMembers, selectedMember?.id]);
  const handleRefresh = async () => {
    await refetchDirectReports();
    await refetchUsersRoutingProfiles();
    await refetchAgentCcpStatus();
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            {t('pages.manager.team.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('pages.manager.team.description')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          {isRefreshing ? t('common.loading') : t('common.refresh')}
        </Button>
      </Box>

      {isRefreshing && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!isRefreshing && (
        <Box sx={{ mt: 2 }}>
          <List disablePadding sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {combinedMembers.map(member => {
              const isYou =
                member.id === user?.id || member.mail === user?.email;
              const pillColor = getStatusPillColor(member.ccpStatus);
              return (
                <ListItem
                  key={member.id}
                  onClick={() => setSelectedMember(member)}
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography fontWeight={600}>
                          {member.displayName}
                        </Typography>
                        {isYou && (
                          <Typography variant="caption" color="text.secondary">
                            ({t('pages.manager.team.you')})
                          </Typography>
                        )}
                      </Stack>
                    }
                  />
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Pill
                      label={getTranslatedCcpStatus(member.ccpStatus)}
                      color={pillColor as never}
                      size="small"
                      variant="filled"
                    />
                  </Stack>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}

      {selectedMember && (
        <TeamMemberQueuePopup
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
        />
      )}
    </Paper>
  );
}
